<template>
  <div class="cashier-order-audit">
    <div class="page-header">
      <h2>收银订单审计</h2>
      <p>集中查看退款、撤销、免费单和异常收银记录</p>
    </div>

    <div class="page-content">
      <div class="search-section">
        <div class="search-box">
          <select v-model="searchStoreId" class="store-select">
            <option value="">全部门店</option>
            <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
          </select>
          <select v-model="auditType" class="store-select">
            <option value="">全部类型</option>
            <option value="REFUND">退款</option>
            <option value="REVOKE">撤销</option>
            <option value="FREE">免费单</option>
            <option value="MEMBER_BALANCE">会员余额扣费</option>
          </select>
          <input v-model="startTime" type="date" class="date-input" />
          <input v-model="endTime" type="date" class="date-input" />
          <button class="search-btn" @click="handleSearch">搜索</button>
          <button class="reset-btn" @click="handleReset">重置</button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-card">
          <span>总记录数</span>
          <strong>{{ auditPagination.totalElements }}</strong>
        </div>
        <div class="summary-card warning">
          <span>本页退款金额</span>
          <strong>{{ formatMoney(refundAmount) }}元</strong>
        </div>
        <div class="summary-card danger">
          <span>本页撤销/免费单</span>
          <strong>{{ revokeCount }} / {{ freeCount }}</strong>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <h3>审计列表</h3>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>订单号</th>
                <th>门店</th>
                <th>会员</th>
                <th>手机号</th>
                <th>金额</th>
                <th>支付方式</th>
                <th>审计类型</th>
                <th>操作人</th>
                <th>时间</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="10" class="empty-cell">加载中...</td>
              </tr>
              <tr v-else-if="orders.length === 0">
                <td colspan="10" class="empty-cell">暂无审计记录</td>
              </tr>
              <tr v-else v-for="order in orders" :key="order.id || order.orderNo">
                <td class="mono-cell">{{ order.orderNo || order.orderId || '-' }}</td>
                <td>{{ order.storeName || getStoreName(order.storeId) }}</td>
                <td>{{ order.customerName || order.memberName || '-' }}</td>
                <td>{{ order.phone || order.customerPhone || '-' }}</td>
                <td :class="['amount-cell', Number(order.amount || 0) < 0 ? 'negative' : '']">
                  {{ formatMoney(order.amount || order.payAmount || 0) }}元
                </td>
                <td>{{ getPayTypeText(order) }}</td>
                <td><span class="audit-tag">{{ getAuditTypeLabel(order) }}</span></td>
                <td>{{ order.operatorName || order.createdBy || '-' }}</td>
                <td>{{ formatDate(order.consumptionTime || order.createdTime || order.payTime) }}</td>
                <td>{{ order.remark || order.refundReason || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pagination-section" v-if="!loading && auditPagination.totalElements > 0">
          <div class="pagination-info">
            共 {{ auditPagination.totalElements }} 条记录，第 {{ auditPagination.page + 1 }} / {{ totalAuditPages }} 页
          </div>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="auditPagination.page === 0" @click="handlePageChange(0)">首页</button>
            <button class="page-btn" :disabled="auditPagination.page === 0" @click="handlePageChange(auditPagination.page - 1)">上一页</button>
            <span class="page-info">{{ auditPagination.page + 1 }} / {{ totalAuditPages }}</span>
            <button class="page-btn" :disabled="auditPagination.page >= totalAuditPages - 1" @click="handlePageChange(auditPagination.page + 1)">下一页</button>
            <button class="page-btn" :disabled="auditPagination.page >= totalAuditPages - 1" @click="handlePageChange(totalAuditPages - 1)">末页</button>
            <select v-model.number="auditPagination.size" class="page-size-select" @change="handlePageSizeChange">
              <option :value="10">10条/页</option>
              <option :value="20">20条/页</option>
              <option :value="50">50条/页</option>
              <option :value="100">100条/页</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'CashierOrderAudit',
  data() {
    return {
      orders: [],
      storeList: [],
      searchStoreId: '',
      auditType: '',
      startTime: '',
      endTime: '',
      loading: false,
      auditPagination: {
        page: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0
      }
    }
  },
  computed: {
    totalAuditPages() {
      return Math.max(1, Number(this.auditPagination.totalPages || 0))
    },
    refundAmount() {
      return this.orders
        .filter(order => this.getAuditType(order) === 'REFUND')
        .reduce((sum, order) => sum + Math.abs(Number(order.amount || order.payAmount || 0)), 0)
    },
    revokeCount() {
      return this.orders.filter(order => this.getAuditType(order) === 'REVOKE').length
    },
    freeCount() {
      return this.orders.filter(order => this.getAuditType(order) === 'FREE').length
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadOrders()
  },
  watch: {
    auditType() {
      this.auditPagination.page = 0
    }
  },
  methods: {
    async loadStoreList() {
      const response = await request.get('/api/stores/getStores', { params: { page: 0, size: 1000 } })
      if (response.data.success && response.data.code === 200) {
        this.storeList = response.data.data.content || []
      }
    },
    async loadOrders() {
      this.loading = true
      try {
        const response = await request.post(`/api/consumption/query?page=${encodeURIComponent(this.auditPagination.page)}&size=${encodeURIComponent(this.auditPagination.size)}&sortBy=consumptionTime&sortDir=desc`, {
          storeId: this.searchStoreId || null,
          auditType: this.auditType || null,
          startTime: this.startTime || null,
          endTime: this.endTime || null
        })
        if (response.data.success && response.data.code === 200) {
          const pageData = response.data.data || {}
          this.orders = pageData.content || []
          this.auditPagination = {
            ...this.auditPagination,
            page: pageData.number !== undefined ? pageData.number : this.auditPagination.page,
            size: pageData.size !== undefined ? pageData.size : this.auditPagination.size,
            totalElements: pageData.totalElements || 0,
            totalPages: pageData.totalPages || 0
          }
        } else {
          throw new Error(response.data.message || '获取审计记录失败')
        }
      } catch (error) {
        console.error('获取审计记录失败:', error)
        this.orders = []
        alert(error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : (error.message || '获取审计记录失败'))
      } finally {
        this.loading = false
      }
    },
    handleReset() {
      this.searchStoreId = ''
      this.auditType = ''
      this.startTime = ''
      this.endTime = ''
      this.auditPagination.page = 0
      this.loadOrders()
    },
    handleSearch() {
      this.auditPagination.page = 0
      this.loadOrders()
    },
    handlePageChange(page) {
      const nextPage = Math.max(0, Math.min(Number(page) || 0, this.totalAuditPages - 1))
      this.auditPagination.page = nextPage
      this.loadOrders()
    },
    handlePageSizeChange() {
      this.auditPagination.page = 0
      this.loadOrders()
    },
    getStoreName(storeId) {
      const store = this.storeList.find(item => String(item.id) === String(storeId))
      return store ? store.storeName : '-'
    },
    getAuditType(order) {
      const orderStatus = String(order.orderStatus || '').toUpperCase()
      const payTypeText = String(order.paymentMethod || order.payTypeName || '').toUpperCase()
      const payType = Number(order.payType)
      const consumerType = Number(order.consumerType)
      if (orderStatus.includes('REFUND') || order.refundAmount) return 'REFUND'
      if (orderStatus.includes('REVOKE') || orderStatus.includes('CANCEL')) return 'REVOKE'
      if (consumerType === 3 || payTypeText.includes('FREE') || Number(order.amount || order.payAmount || 0) === 0) return 'FREE'
      if (payType === 2 || payTypeText.includes('MEMBER') || payTypeText.includes('BALANCE') || payTypeText.includes('余额')) return 'MEMBER_BALANCE'
      return 'NORMAL'
    },
    getAuditTypeLabel(order) {
      const labels = {
        REFUND: '退款',
        REVOKE: '撤销',
        FREE: '免费单',
        MEMBER_BALANCE: '会员余额',
        NORMAL: '普通消费'
      }
      return labels[this.getAuditType(order)] || '普通消费'
    },
    getPayTypeText(order) {
      if (order.paymentMethod || order.payTypeName) {
        return order.paymentMethod || order.payTypeName
      }
      const payTypeMap = {
        0: '扫码',
        1: '现金',
        2: '会员余额',
        3: '混合支付'
      }
      return payTypeMap[Number(order.payType)] || '-'
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.cashier-order-audit {
  padding: 24px;
  background: #f0f2f5;
  min-height: calc(100vh - 64px);
}
.page-header { margin-bottom: 24px; }
.page-header h2 { margin: 0 0 8px; color: #1f2f3d; font-weight: 600; }
.page-header p { margin: 0; color: #606266; font-size: 14px; }
.page-content { background: transparent; }
.search-section { 
  background: #fff; 
  padding: 20px; 
  border-radius: 8px; 
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  margin-bottom: 20px; 
}
.search-box { display: flex; flex-wrap: wrap; gap: 16px; align-items: center; }
.store-select, .date-input { 
  height: 36px; 
  padding: 0 12px; 
  border: 1px solid #dcdfe6; 
  border-radius: 4px; 
  color: #606266;
  transition: border-color 0.2s;
}
.store-select:focus, .date-input:focus {
  border-color: #409eff;
  outline: none;
}
button { 
  border: none; 
  border-radius: 4px; 
  padding: 0 20px; 
  height: 36px;
  cursor: pointer; 
  font-weight: 500;
  transition: all 0.3s;
}
.search-btn { background: #409eff; color: #fff; }
.search-btn:hover { background: #66b1ff; }
.reset-btn { background: #fff; color: #606266; border: 1px solid #dcdfe6; }
.reset-btn:hover { color: #409eff; border-color: #c6e2ff; background-color: #ecf5ff; }

.summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
.summary-card { 
  padding: 24px; 
  background: #fff; 
  border-radius: 8px; 
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.summary-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #409eff;
}
.summary-card.warning::before { background: #e6a23c; }
.summary-card.danger::before { background: #f56c6c; }

.summary-card span { color: #909399; font-size: 14px; margin-bottom: 8px; }
.summary-card strong { font-size: 28px; color: #303133; font-weight: 600; }
.summary-card.warning strong { color: #e6a23c; }
.summary-card.danger strong { color: #f56c6c; }

.table-section { 
  background: #fff; 
  border-radius: 8px; 
  padding: 24px; 
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}
.table-header { margin-bottom: 16px; }
.table-header h3 { margin: 0; color: #303133; font-size: 16px; font-weight: 600; }
.table-container { overflow-x: auto; }
.data-table { width: 100%; min-width: 1200px; border-collapse: collapse; }
.data-table th, .data-table td { padding: 14px 12px; border-bottom: 1px solid #ebeef5; text-align: left; font-size: 14px; }
.data-table th { background-color: #f5f7fa; color: #909399; font-weight: 500; }
.data-table tbody tr:hover { background-color: #f5f7fa; }
.empty-cell { text-align: center; color: #909399; padding: 60px; }
.mono-cell { font-family: Consolas, Monaco, monospace; color: #606266; font-size: 13px; }
.amount-cell { color: #67c23a; font-weight: 600; }
.amount-cell.negative { color: #f56c6c; }
.audit-tag { 
  display: inline-block;
  padding: 4px 10px; 
  background: #ecf5ff; 
  color: #409eff; 
  border: 1px solid #d9ecff;
  border-radius: 4px; 
  font-size: 12px; 
}
.pagination-section { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  margin-top: 24px; 
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}
.pagination-info { color: #606266; font-size: 14px; }
.pagination-controls { display: flex; align-items: center; gap: 8px; }
.page-btn { 
  background: #fff; 
  color: #606266; 
  border: 1px solid #dcdfe6; 
  height: 32px;
  padding: 0 12px;
  border-radius: 4px;
}
.page-btn:hover:not(:disabled) { color: #409eff; border-color: #c6e2ff; background-color: #ecf5ff; }
.page-btn:disabled { color: #c0c4cc; cursor: not-allowed; background: #f5f7fa; border-color: #e4e7ed; }
.page-info { color: #606266; min-width: 60px; text-align: center; font-size: 14px; }
.page-size-select { 
  height: 32px; 
  padding: 0 8px; 
  border: 1px solid #dcdfe6; 
  border-radius: 4px; 
  color: #606266;
}
@media (max-width: 900px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
